import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, SIGNED_OUT_NOTICE_KEY } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { describeError } from "@/lib/api";
import { takeReturnTo } from "@/lib/returnTo";
import { toast } from "sonner";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, playAsGuest } = useAuth();
  const [guestLoading, setGuestLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  // The phone app always stays signed in, so the box is website only.
  const inApp = !!window.Capacitor?.isNativePlatform?.();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Set when this device was signed out because the account signed in on
  // another device. Shown once.
  const [notice] = useState(() => {
    try {
      const n = sessionStorage.getItem(SIGNED_OUT_NOTICE_KEY);
      sessionStorage.removeItem(SIGNED_OUT_NOTICE_KEY);
      return n || "";
    } catch { return ""; }
  });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password, inApp || remember);
      toast.success("Welcome back!");
      const from = location.state?.from;
      const dest = takeReturnTo() || (from?.pathname ? from.pathname + (from.search || "") : "/lobby");
      navigate(dest, { replace: true });
    } catch (err) {
      const msg = describeError(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const startGuest = async () => {
    setError("");
    setGuestLoading(true);
    try {
      await playAsGuest();
      navigate("/avatar?welcome=1", { replace: true });
    } catch (err) {
      setError(describeError(err));
    } finally {
      setGuestLoading(false);
    }
  };

  return (
    <main className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md" data-testid="login-page">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl font-black tracking-tighter">Welcome back</h1>
          <p className="text-muted-foreground mt-2">Sign in to find your next match.</p>
        </div>

        <form onSubmit={submit} className="bg-surface border border-border rounded-2xl p-8 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
              Email or username
            </Label>
            <Input
              id="email"
              type="text"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-background h-11"
              data-testid="login-email-input"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">
                Password
              </Label>
              <Link
                to={email.trim() ? `/forgot-password?email=${encodeURIComponent(email.trim())}` : "/forgot-password"}
                className="text-xs text-muted-foreground underline-offset-4 hover:underline hover:text-foreground"
                data-testid="login-forgot-password-link"
              >
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-background h-11"
              data-testid="login-password-input"
            />
          </div>

          {!inApp && (
          <div className="flex items-center gap-2">
            <Checkbox
              id="remember"
              checked={remember}
              onCheckedChange={(v) => setRemember(v === true)}
              data-testid="login-remember-checkbox"
            />
            <Label htmlFor="remember" className="text-sm font-normal text-muted-foreground cursor-pointer">
              Remember me
            </Label>
          </div>
          )}

          {notice && !error && (
            <div
              className="text-sm text-foreground bg-muted border border-border rounded-lg px-3 py-2"
              data-testid="login-session-notice"
            >
              {notice}. Sign in again to play here; that will sign the other device out.
            </div>
          )}

          {error && (
            <div
              className="text-sm text-destructive bg-destructive/10 border border-destructive/30 rounded-lg px-3 py-2"
              data-testid="login-error"
            >
              {error}
              {/wrong password/i.test(error) && (
                <Link
                  to={`/forgot-password?email=${encodeURIComponent(email.trim())}`}
                  className="block mt-1 underline underline-offset-4"
                  data-testid="login-error-reset-link"
                >
                  Reset your password
                </Link>
              )}
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-full bg-brand hover:bg-brand-hover text-brand-foreground font-semibold"
            data-testid="login-submit-button"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>

          <div className="text-center text-sm text-muted-foreground pt-2">
            New to Jangle?{" "}
            <Link to="/register" className="text-foreground underline-offset-4 hover:underline" data-testid="login-to-register-link">
              Create an account
            </Link>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground" aria-hidden="true">
            <div className="flex-1 h-px bg-border" />or<div className="flex-1 h-px bg-border" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={startGuest}
            disabled={guestLoading || loading}
            className="w-full h-11 rounded-full font-display font-extrabold text-opp border-2 border-opp bg-transparent hover:bg-opp/10 hover:text-opp"
            data-testid="login-guest-button"
          >
            {guestLoading ? "Starting…" : "Play as guest"}
          </Button>
          <p className="text-xs text-muted-foreground text-center -mt-2">
            No account needed. Play the bots and the daily puzzle, and save your progress with an account any time.
          </p>
        </form>
      </div>
    </main>
  );
}
